import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const APP_TITLE = "BitcoinAgree Tracker V1.0.1";
const TRACKER_KEY_STORAGE = 'bitcoinagree_tracker_key';

function getApiUrl() {
  var hostname = window.location.hostname;
  if (hostname === 'tracker.test.local' || hostname === 'localhost' || hostname === '127.0.0.1') {
    return "http://tracker-api.test.local";
  }
  return "https://tracker-api.bitcoinagree.com";
}

function getStoredTrackerKey() {
  try {
    return window.sessionStorage.getItem(TRACKER_KEY_STORAGE) || '';
  } catch (err) {
    return '';
  }
}

function storeTrackerKey(key) {
  try {
    window.sessionStorage.setItem(TRACKER_KEY_STORAGE, key);
  } catch (err) {
    // Private browsing or locked storage should not block access.
  }
}

function formatDate(dateString) {
  var date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatNumber(num, decimals) {
  if (!decimals) decimals = 2;
  return num.toLocaleString('en-US', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
}

function getYearStartTimestamp(year) {
  return Date.UTC(year, 0, 1) / 1000;
}

function getChartStartTimestamp(items) {
  if (!items.length) return 0;
  var firstDate = new Date(items[0].timestamp * 1000);
  return getYearStartTimestamp(firstDate.getUTCFullYear());
}

function getChartEndTimestamp(items) {
  if (!items.length) return 0;
  var lastDate = new Date(items[items.length - 1].timestamp * 1000);
  return getYearStartTimestamp(lastDate.getUTCFullYear() + 1);
}

function getYearTicks(items) {
  if (!items.length) return [];

  var firstYear = new Date(items[0].timestamp * 1000).getUTCFullYear();
  var lastYear = new Date(items[items.length - 1].timestamp * 1000).getUTCFullYear();
  var ticks = [];

  for (var year = firstYear; year <= lastYear + 1; year++) {
    ticks.push({ value: getYearStartTimestamp(year) });
  }

  return ticks;
}

function CustomDot(props) {
  var cx = props.cx;
  var cy = props.cy;
  var payload = props.payload;
  
  // DCA projected point - show as hollow circle
  if (payload.isDca) {
    return React.createElement('circle', {
      cx: cx,
      cy: cy,
      r: 6,
      fill: 'none',
      stroke: '#ff9800',
      strokeWidth: 2,
      opacity: 0.7
    });
  }
  
  if (!payload.event) {
    return null;
  }

  var eventType = payload.event.type;
  if (eventType !== 'buy' && eventType !== 'sell') {
    return null;
  }

  var color = eventType === 'buy' ? '#4caf50' : '#f44336';
  
  // Scale circle size based on transaction quantity (BTC amount)
  var quantity = payload.event.quantity || 0;
  // Min radius 4, max radius 24, scaled by quantity for better visibility
  var baseRadius = Math.min(24, Math.max(4, 4 + (Math.abs(quantity) * 300)));
  
  return React.createElement('circle', {
    cx: cx,
    cy: cy,
    r: baseRadius,
    fill: color,
    stroke: 'none',
    opacity: 0.8
  });
}

function CustomTooltip(props) {
  var active = props.active;
  var payload = props.payload;
  
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  var data = payload[0].payload;
  
  // DCA projected data tooltip
  if (data.isDca) {
    return React.createElement('div', {
      style: {
        backgroundColor: '#fff3e0',
        border: '2px solid #ff9800',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }
    },
      React.createElement('p', { style: { margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px', color: '#ff9800' } },
        'DCA Projection'
      ),
      React.createElement('p', { style: { margin: '0 0 8px 0', fontSize: '13px' } },
        formatDate(data.date)
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '13px' } },
        'Projected BTC: ', React.createElement('strong', null, formatNumber(data.btc_holdings, 8))
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '13px' } },
        'Projected USD: $', React.createElement('strong', null, formatNumber(data.usd_holdings, 2))
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '13px', color: '#666' } },
        'BTC Price: $', React.createElement('strong', null, formatNumber(data.btc_price, 2))
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '13px', color: '#666' } },
        'Avg Cost Basis: $', React.createElement('strong', null, formatNumber(data.avg_holding_price, 2))
      )
    );
  }
  
  var event = data.event;

  return React.createElement('div', {
    style: {
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      borderRadius: '8px',
      padding: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  },
    React.createElement('p', { style: { margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px' } },
      formatDate(data.date)
    ),
    React.createElement('p', { style: { margin: '4px 0', fontSize: '13px' } },
      'BTC Holdings: ', React.createElement('strong', null, formatNumber(data.btc_holdings, 8))
    ),
    React.createElement('p', { style: { margin: '4px 0', fontSize: '13px' } },
      'USD Value: $', React.createElement('strong', null, formatNumber(data.usd_holdings, 2))
    ),
    data.avg_holding_price && React.createElement('p', { style: { margin: '4px 0', fontSize: '13px', color: '#666' } },
      'Avg Buy Price: $', React.createElement('strong', null, formatNumber(data.avg_holding_price, 2))
    ),
    event && (event.type === 'buy' || event.type === 'sell') && React.createElement('div', {
      style: {
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid #eee'
      }
    },
      React.createElement('p', {
        style: {
          margin: '4px 0',
          fontWeight: 'bold',
          color: event.type === 'buy' ? '#4caf50' : '#f44336'
        }
      },
        event.type === 'buy' ? '✓ BUY' : '✗ SELL'
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '12px' } },
        'Amount: ', formatNumber(event.quantity, 8), ' BTC'
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '12px' } },
        'Price Then: $', formatNumber(event.price_at_transaction, 2)
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '12px' } },
        'Value Then: $', formatNumber(event.usd_at_transaction, 2)
      ),
      React.createElement('p', { style: { margin: '4px 0', fontSize: '12px' } },
        'Value Now: $', formatNumber(event.current_value, 2)
      ),
      React.createElement('p', {
        style: {
          margin: '4px 0',
          fontSize: '13px',
          fontWeight: 'bold',
          color: event.usd_at_transaction > 0 && event.current_value / event.usd_at_transaction >= 1 ? '#4caf50' : '#f44336'
        }
      },
        event.usd_at_transaction > 0 && event.current_value / event.usd_at_transaction >= 1 ? '+' : '',
        event.usd_at_transaction > 0 ? formatNumber((event.current_value / event.usd_at_transaction - 1) * 100, 1) : '0.0',
        '% (',
        event.usd_at_transaction > 0 ? formatNumber(event.current_value / event.usd_at_transaction, 2) : '0.00',
        'x)'
      )
    )
  );
}

export default function App() {
  var stateHook = useState([]);
  var holdings = stateHook[0];
  var setHoldings = stateHook[1];
  
  var priceHook = useState(0);
  var currentPrice = priceHook[0];
  var setCurrentPrice = priceHook[1];
  
  var btcHook = useState(0);
  var totalBTC = btcHook[0];
  var setTotalBTC = btcHook[1];
  
  var usdHook = useState(0);
  var totalUSD = usdHook[0];
  var setTotalUSD = usdHook[1];
  
  var investedHook = useState(0);
  var totalInvested = investedHook[0];
  var setTotalInvested = investedHook[1];
  
  var avgYearHook = useState(0);
  var avgPerYear = avgYearHook[0];
  var setAvgPerYear = avgYearHook[1];
  
  var avgGainYearHook = useState(0);
  var avgGainPerYear = avgGainYearHook[0];
  var setAvgGainPerYear = avgGainYearHook[1];
  
  var loadingHook = useState(true);
  var loading = loadingHook[0];
  var setLoading = loadingHook[1];
  
  var errorHook = useState(null);
  var error = errorHook[0];
  var setError = errorHook[1];

  var trackerKeyHook = useState(getStoredTrackerKey);
  var trackerKey = trackerKeyHook[0];
  var setTrackerKey = trackerKeyHook[1];

  var unlockedHook = useState(false);
  var unlocked = unlockedHook[0];
  var setUnlocked = unlockedHook[1];

  var authErrorHook = useState('');
  var authError = authErrorHook[0];
  var setAuthError = authErrorHook[1];
  
  var viewHook = useState('btc');
  var viewMode = viewHook[0];
  var setViewMode = viewHook[1];
  
  var startDateHook = useState('');
  var startDate = startDateHook[0];
  var setStartDate = startDateHook[1];
  
  var endDateHook = useState('');
  var endDate = endDateHook[0];
  var setEndDate = endDateHook[1];
  
  var selectedYearHook = useState(null);
  var selectedYear = selectedYearHook[0];
  var setSelectedYear = selectedYearHook[1];
  
  var dcaAmountHook = useState('1000');
  var dcaAmount = dcaAmountHook[0];
  var setDcaAmount = dcaAmountHook[1];
  
  var dcaIntervalHook = useState('monthly');
  var dcaInterval = dcaIntervalHook[0];
  var setDcaInterval = dcaIntervalHook[1];
  
  var dcaDurationHook = useState('3');
  var dcaDuration = dcaDurationHook[0];
  var setDcaDuration = dcaDurationHook[1];
  
  var dcaAprHook = useState('20');
  var dcaApr = dcaAprHook[0];
  var setDcaApr = dcaAprHook[1];
  
  var dcaActiveHook = useState(false);
  var dcaActive = dcaActiveHook[0];
  var setDcaActive = dcaActiveHook[1];
  
  var dcaDataHook = useState([]);
  var dcaData = dcaDataHook[0];
  var setDcaData = dcaDataHook[1];

  function loadHoldings(accessKey) {
    var apiUrl = getApiUrl();
    var headers = {};
    if (accessKey) {
      headers['X-Tracker-Key'] = accessKey;
    }

    setLoading(true);
    setError(null);
    setAuthError('');

    fetch(apiUrl + '/api/holdings', { headers: headers })
      .then(function(response) {
        if (response.status === 401) {
          throw new Error('unauthorized');
        }
        if (!response.ok) {
          throw new Error('Failed to fetch holdings data');
        }
        return response.json();
      })
      .then(function(data) {
        setHoldings(data.holdings || []);
        setCurrentPrice(data.current_price || 0);
        setTotalBTC(data.current_btc_holdings || 0);
        setTotalUSD(data.current_usd_value || 0);
        setTotalInvested(data.total_invested || 0);
        setAvgPerYear(data.avg_per_year || 0);
        setAvgGainPerYear(data.avg_gain_per_year || 0);
        setUnlocked(true);
        storeTrackerKey(accessKey);
        if (data.holdings && data.holdings.length > 0) {
          setStartDate(data.holdings[0].date.split('T')[0]);
          setEndDate(data.holdings[data.holdings.length - 1].date.split('T')[0]);
        }
        setLoading(false);
      })
      .catch(function(err) {
        if (err.message === 'unauthorized') {
          setUnlocked(false);
          setAuthError('That key did not work.');
        } else {
          setError(err.message);
        }
        setLoading(false);
      });
  }

  useEffect(function() {
    document.title = APP_TITLE;

    var storedKey = getStoredTrackerKey();
    if (storedKey) {
      setTrackerKey(storedKey);
      loadHoldings(storedKey);
    } else {
      setLoading(false);
    }
  }, []);

  function unlockTracker(event) {
    event.preventDefault();
    var cleanKey = trackerKey.trim();
    if (!cleanKey) {
      setAuthError('Enter the access key.');
      return;
    }
    loadHoldings(cleanKey);
  }
  
  // Generate DCA projection data
  function generateDcaProjections() {
    if (!dcaActive || holdings.length === 0) return [];
    
    var projections = [];
    var lastHolding = holdings[holdings.length - 1];
    var startBTC = lastHolding.btc_holdings;
    var startCostBasis = lastHolding.total_cost_basis;
    var startPrice = lastHolding.btc_price;
    var startTimestamp = lastHolding.timestamp;
    
    var amountPerPurchase = parseFloat(dcaAmount) || 100;
    var durationYears = parseFloat(dcaDuration) || 1;
    var aprPercent = parseFloat(dcaApr) || 20;
    var intervalDays = dcaInterval === 'weekly' ? 7 : 30;
    var totalPurchases = Math.floor((durationYears * 365) / intervalDays);
    
    var currentBTC = startBTC;
    var currentCostBasis = startCostBasis;
    var currentTimestamp = startTimestamp;
    
    for (var i = 1; i <= totalPurchases; i++) {
      currentTimestamp += intervalDays * 24 * 3600;
      var yearsFromStart = (intervalDays * i) / 365;
      var projectedPrice = startPrice * Math.pow(1 + aprPercent / 100, yearsFromStart);
      var btcPurchased = amountPerPurchase / projectedPrice;
      
      currentBTC += btcPurchased;
      currentCostBasis += amountPerPurchase;
      
      var futureDate = new Date(currentTimestamp * 1000);
      projections.push({
        date: futureDate.toISOString(),
        timestamp: currentTimestamp,
        btc_holdings: currentBTC,
        usd_holdings: currentBTC * projectedPrice,
        btc_price: projectedPrice,
        avg_holding_price: currentCostBasis / currentBTC,
        total_cost_basis: currentCostBasis,
        isDca: true
      });
    }
    
    return projections;
  }
  
  var allHoldings = dcaActive ? holdings.concat(generateDcaProjections()) : holdings;
  
  var filteredHoldings = allHoldings.filter(function(item) {
    // When DCA is active, show all data including future projections
    if (dcaActive) return true;
    // Otherwise filter by date range if set
    if (!startDate || !endDate) return true;
    var itemDate = item.date.split('T')[0];
    return itemDate >= startDate && itemDate <= endDate;
  });
  
  // Calculate values based on filtered data
  var displayBTC = filteredHoldings.length > 0 ? filteredHoldings[filteredHoldings.length - 1].btc_holdings : totalBTC;
  var displayUSD = filteredHoldings.length > 0 ? filteredHoldings[filteredHoldings.length - 1].usd_holdings : totalUSD;
  var displayInvested = filteredHoldings.length > 0 ? filteredHoldings[filteredHoldings.length - 1].total_cost_basis : totalInvested;
  
  // Calculate gain/loss for the filtered range
  var displayTotalGain;  // From beginning to end date
  var displayCurrentGain;  // From start date to end date
  var displayBTCBought;
  var displayCurrentInvested;
  var displayCurrentUSDValue;
  if (filteredHoldings.length > 0 && startDate && endDate) {
    var endCostBasis = filteredHoldings[filteredHoldings.length - 1].total_cost_basis;
    var endUsdValue = filteredHoldings[filteredHoldings.length - 1].usd_holdings;
    var endBTC = filteredHoldings[filteredHoldings.length - 1].btc_holdings;
    
    // Find holdings before the start date to get accurate starting point
    var startCostBasis = 0;
    var startUsdValue = 0;
    var startBTC = 0;
    
    // Look for last transaction before start date
    for (var i = 0; i < holdings.length; i++) {
      var itemDate = holdings[i].date.split('T')[0];
      if (itemDate < startDate) {
        startCostBasis = holdings[i].total_cost_basis;
        startUsdValue = holdings[i].usd_holdings;
        startBTC = holdings[i].btc_holdings;
      } else {
        break;
      }
    }
    
    var investedInRange = endCostBasis - startCostBasis;
    var valueChangeInRange = endUsdValue - startUsdValue;
    
    // Current Gain/Loss: from start date to end date
    displayCurrentGain = valueChangeInRange - investedInRange;
    
    // Total Gain/Loss: from beginning to end date
    displayTotalGain = endUsdValue - endCostBasis;
    
    displayBTCBought = endBTC - startBTC;
    displayCurrentInvested = investedInRange;
    displayCurrentUSDValue = displayBTCBought * currentPrice;
  } else {
    displayTotalGain = displayUSD - displayInvested;
    displayCurrentGain = displayUSD - displayInvested;
    displayBTCBought = displayBTC;
    displayCurrentInvested = displayInvested;
    displayCurrentUSDValue = displayUSD;
  }

  // Calculate displayed Bitcoin price (current or projected if DCA active)
  var displayPrice = currentPrice;
  if (dcaActive && holdings.length > 0) {
    var lastHolding = holdings[holdings.length - 1];
    var startPrice = lastHolding.btc_price;
    var durationYears = parseFloat(dcaDuration) || 1;
    var aprPercent = parseFloat(dcaApr) || 20;
    displayPrice = startPrice * Math.pow(1 + aprPercent / 100, durationYears);
  }
  
  // Calculate Bitcoin price statistics from filtered data
  var btcLowPrice = 0;
  var btcHighPrice = 0;
  var btcAvgPrice = 0;
  if (filteredHoldings.length > 0) {
    var minPrice = filteredHoldings[0].btc_price;
    var maxPrice = filteredHoldings[0].btc_price;
    var totalPrice = 0;
    for (var i = 0; i < filteredHoldings.length; i++) {
      var price = filteredHoldings[i].btc_price;
      if (price < minPrice) minPrice = price;
      if (price > maxPrice) maxPrice = price;
      totalPrice += price;
    }
    btcLowPrice = minPrice;
    btcHighPrice = maxPrice;
    btcAvgPrice = totalPrice / filteredHoldings.length;
  }

  if (!unlocked) {
    return React.createElement('div', {
      style: {
        fontFamily: 'Arial, sans-serif',
        minHeight: '100vh',
        backgroundColor: '#111827',
        color: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }
    },
      React.createElement('form', {
        onSubmit: unlockTracker,
        style: {
          width: '100%',
          maxWidth: '420px',
          backgroundColor: '#ffffff',
          color: '#111827',
          padding: '28px',
          borderRadius: '8px',
          boxShadow: '0 20px 45px rgba(0,0,0,0.25)'
        }
      },
        React.createElement('h1', {
          style: {
            margin: '0 0 8px 0',
            color: '#f7931a',
            fontSize: '28px'
          }
        }, 'Coming soon'),
        React.createElement('p', {
          style: {
            margin: '0 0 22px 0',
            color: '#4b5563',
            lineHeight: 1.5
          }
        }, 'Enter the access key to open the tracker.'),
        React.createElement('input', {
          type: 'password',
          value: trackerKey,
          onChange: function(event) { setTrackerKey(event.target.value); },
          placeholder: 'Access key',
          autoComplete: 'current-password',
          style: {
            width: '100%',
            boxSizing: 'border-box',
            padding: '12px 14px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '16px',
            marginBottom: '12px'
          }
        }),
        authError ? React.createElement('div', {
          style: {
            color: '#b91c1c',
            fontSize: '14px',
            marginBottom: '12px'
          }
        }, authError) : null,
        error ? React.createElement('div', {
          style: {
            color: '#b91c1c',
            fontSize: '14px',
            marginBottom: '12px'
          }
        }, error) : null,
        React.createElement('button', {
          type: 'submit',
          disabled: loading,
          style: {
            width: '100%',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: loading ? '#fbbf24' : '#f7931a',
            color: '#111827',
            cursor: loading ? 'default' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            padding: '12px 14px'
          }
        }, loading ? 'Checking...' : 'Open tracker')
      )
    );
  }

  return React.createElement('div', {
    style: {
      fontFamily: 'Arial, sans-serif',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }
  },
    React.createElement('div', {
      style: {
        backgroundColor: '#fff',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }
    },
      React.createElement('h1', {
        style: {
          margin: '0 0 10px 0',
          color: '#f7931a',
          fontSize: '32px'
        }
      }, '₿ Bitcoin Holdings Tracker'),
      
      React.createElement('div', {
        style: {
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }
      },
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 
            dcaActive ? 'Bitcoin Price (Projected ' + (2026 + (parseInt(dcaDuration) || 3)) + ')' : 'Bitcoin Price'
          ),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#f7931a' } },
            '$', formatNumber(displayPrice, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Bitcoin All Time Low'),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#ff5722' } },
            '$', formatNumber(btcLowPrice, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Bitcoin All Time High'),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#4caf50' } },
            '$', formatNumber(btcHighPrice, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Bitcoin AVG Price'),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#2196f3' } },
            '$', formatNumber(btcAvgPrice, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #f7931a'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Total BTC Holdings'),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#f7931a' } },
            '\u20BF ', formatNumber(displayBTC, 8)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Total USD Value'),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#4caf50' } },
            '$', formatNumber(displayUSD, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Total Invested'),
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#333' } },
            '$', formatNumber(displayInvested, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid ' + (displayTotalGain >= 0 ? '#4caf50' : '#f44336')
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Total Gain/Loss'),
          React.createElement('div', { 
            style: { 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: displayTotalGain >= 0 ? '#4caf50' : '#f44336'
            } 
          },
            displayTotalGain >= 0 ? '+' : '',
            '$', formatNumber(displayTotalGain, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #f7931a'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Current BTC Holding'),
          React.createElement('div', { 
            style: { 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#f7931a'
            } 
          },
            '\u20BF ', 
            displayBTCBought >= 0 ? '+' : '',
            formatNumber(displayBTCBought, 8)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #4caf50'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Current USD Value'),
          React.createElement('div', { 
            style: { 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#4caf50'
            } 
          },
            '$', formatNumber(displayCurrentUSDValue, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Current Invested'),
          React.createElement('div', { 
            style: { 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#333'
            } 
          },
            '$', formatNumber(displayCurrentInvested, 2)
          )
        ),
        React.createElement('div', {
          style: {
            flex: '1',
            minWidth: '200px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid ' + (displayCurrentGain >= 0 ? '#4caf50' : '#f44336')
          }
        },
          React.createElement('div', { style: { fontSize: '12px', color: '#666', marginBottom: '5px' } }, 'Current Gain/Loss'),
          React.createElement('div', { 
            style: { 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: displayCurrentGain >= 0 ? '#4caf50' : '#f44336'
            } 
          },
            displayCurrentGain >= 0 ? '+' : '',
            '$', formatNumber(displayCurrentGain, 2)
          )
        )
      ),

      React.createElement('div', {
        style: {
          marginBottom: '20px',
          display: 'flex',
          gap: '10px'
        }
      },
        React.createElement('button', {
          onClick: function() { setViewMode('btc'); },
          style: {
            padding: '10px 20px',
            backgroundColor: viewMode === 'btc' ? '#f7931a' : '#fff',
            color: viewMode === 'btc' ? '#fff' : '#333',
            border: '1px solid #f7931a',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }
        }, 'BTC Only'),
        React.createElement('button', {
          onClick: function() { setViewMode('both'); },
          style: {
            padding: '10px 20px',
            backgroundColor: viewMode === 'both' ? '#f7931a' : '#fff',
            color: viewMode === 'both' ? '#fff' : '#333',
            border: '1px solid #f7931a',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }
        }, 'Both (USD & BTC)'),
        React.createElement('button', {
          onClick: function() { setViewMode('usd'); },
          style: {
            padding: '10px 20px',
            backgroundColor: viewMode === 'usd' ? '#f7931a' : '#fff',
            color: viewMode === 'usd' ? '#fff' : '#333',
            border: '1px solid #f7931a',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }
        }, 'USD Only')
      ),

      React.createElement('div', {
        style: {
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          display: 'flex',
          gap: '20px',
          alignItems: 'center'
        }
      },
        React.createElement('label', {
          style: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#333'
          }
        }, 'Start Date:'),
        React.createElement('input', {
          type: 'date',
          value: startDate,
          onChange: function(e) { setStartDate(e.target.value); },
          style: {
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }
        }),
        React.createElement('label', {
          style: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#333'
          }
        }, 'End Date:'),
        React.createElement('input', {
          type: 'date',
          value: endDate,
          onChange: function(e) { setEndDate(e.target.value); },
          style: {
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }
        }),
        React.createElement('div', {
          style: {
            display: 'flex',
            gap: '8px',
            marginLeft: '20px',
            alignItems: 'center'
          }
        },
          React.createElement('span', {
            style: {
              fontSize: '14px',
              fontWeight: '500',
              color: '#666',
              marginRight: '5px'
            }
          }, 'Quick:'),
          React.createElement('button', {
            onClick: function() { setStartDate('2022-01-01'); setEndDate('2022-12-31'); setSelectedYear('2022'); },
            style: {
              padding: '6px 12px',
              backgroundColor: selectedYear === '2022' ? '#f7931a' : '#fff',
              color: selectedYear === '2022' ? '#fff' : '#333',
              border: '1px solid #f7931a',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }
          }, '2022'),
          React.createElement('button', {
            onClick: function() { setStartDate('2023-01-01'); setEndDate('2023-12-31'); setSelectedYear('2023'); },
            style: {
              padding: '6px 12px',
              backgroundColor: selectedYear === '2023' ? '#f7931a' : '#fff',
              color: selectedYear === '2023' ? '#fff' : '#333',
              border: '1px solid #f7931a',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }
          }, '2023'),
          React.createElement('button', {
            onClick: function() { setStartDate('2024-01-01'); setEndDate('2024-12-31'); setSelectedYear('2024'); },
            style: {
              padding: '6px 12px',
              backgroundColor: selectedYear === '2024' ? '#f7931a' : '#fff',
              color: selectedYear === '2024' ? '#fff' : '#333',
              border: '1px solid #f7931a',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }
          }, '2024'),
          React.createElement('button', {
            onClick: function() { setStartDate('2025-01-01'); setEndDate('2025-12-31'); setSelectedYear('2025'); },
            style: {
              padding: '6px 12px',
              backgroundColor: selectedYear === '2025' ? '#f7931a' : '#fff',
              color: selectedYear === '2025' ? '#fff' : '#333',
              border: '1px solid #f7931a',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }
          }, '2025'),
          React.createElement('button', {
            onClick: function() { setStartDate('2026-01-01'); setEndDate('2026-12-31'); setSelectedYear('2026'); },
            style: {
              padding: '6px 12px',
              backgroundColor: selectedYear === '2026' ? '#f7931a' : '#fff',
              color: selectedYear === '2026' ? '#fff' : '#333',
              border: '1px solid #f7931a',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }
          }, '2026'),
          React.createElement('button', {
            onClick: function() { setStartDate(''); setEndDate(''); setSelectedYear(null); },
            style: {
              padding: '6px 12px',
              backgroundColor: '#fff',
              color: '#333',
              border: '1px solid #f7931a',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }
          }, 'Reset')
        )
      ),
      
      React.createElement('div', {
        style: {
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #f7931a'
        }
      },
        React.createElement('h3', {
          style: {
            margin: '0 0 15px 0',
            color: '#f7931a',
            fontSize: '16px'
          }
        }, 'DCA Simulation (Dollar Cost Averaging)'),
        React.createElement('div', {
          style: {
            display: 'flex',
            gap: '15px',
            flexWrap: 'wrap',
            alignItems: 'flex-end'
          }
        },
          React.createElement('div', { style: { flex: '1', minWidth: '120px' } },
            React.createElement('label', { style: { display: 'block', fontSize: '12px', marginBottom: '5px', color: '#666' } }, 'Amount (USD)'),
            React.createElement('input', {
              type: 'number',
              value: dcaAmount,
              onChange: function(e) { setDcaAmount(e.target.value); },
              style: {
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }
            })
          ),
          React.createElement('div', { style: { flex: '1', minWidth: '120px' } },
            React.createElement('label', { style: { display: 'block', fontSize: '12px', marginBottom: '5px', color: '#666' } }, 'Interval'),
            React.createElement('select', {
              value: dcaInterval,
              onChange: function(e) { setDcaInterval(e.target.value); },
              style: {
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }
            },
              React.createElement('option', { value: 'weekly' }, 'Weekly'),
              React.createElement('option', { value: 'monthly' }, 'Monthly')
            )
          ),
          React.createElement('div', { style: { flex: '1', minWidth: '120px' } },
            React.createElement('label', { style: { display: 'block', fontSize: '12px', marginBottom: '5px', color: '#666' } }, 'Duration (Years)'),
            React.createElement('select', {
              value: dcaDuration,
              onChange: function(e) { setDcaDuration(e.target.value); },
              style: {
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }
            },
              React.createElement('option', { value: '1' }, '1 Year - 2027'),
              React.createElement('option', { value: '2' }, '2 Years - 2028'),
              React.createElement('option', { value: '3' }, '3 Years - 2029'),
              React.createElement('option', { value: '4' }, '4 Years - 2030'),
              React.createElement('option', { value: '5' }, '5 Years - 2031'),
              React.createElement('option', { value: '6' }, '6 Years - 2032'),
              React.createElement('option', { value: '7' }, '7 Years - 2033'),
              React.createElement('option', { value: '8' }, '8 Years - 2034'),
              React.createElement('option', { value: '9' }, '9 Years - 2035'),
              React.createElement('option', { value: '10' }, '10 Years - 2036')
            )
          ),
          React.createElement('div', { style: { flex: '1', minWidth: '120px' } },
            React.createElement('label', { style: { display: 'block', fontSize: '12px', marginBottom: '5px', color: '#666' } }, 'BTC APR Growth (%)'),
            React.createElement('input', {
              type: 'number',
              value: dcaApr,
              onChange: function(e) { setDcaApr(e.target.value); },
              style: {
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }
            })
          ),
          React.createElement('button', {
            onClick: function() { setDcaActive(true); },
            style: {
              padding: '8px 20px',
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          }, 'Calculate'),
          dcaActive && React.createElement('button', {
            onClick: function() { setDcaActive(false); },
            style: {
              padding: '8px 20px',
              backgroundColor: '#f44336',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          }, 'Reset DCA')
        )
      ),

      loading && React.createElement('div', {
        style: {
          textAlign: 'center',
          padding: '40px',
          fontSize: '16px',
          color: '#666'
        }
      }, 'Loading holdings data...'),

      error && React.createElement('div', {
        style: {
          padding: '20px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '8px',
          marginTop: '20px'
        }
      }, 'Error: ', error),

      !loading && !error && filteredHoldings.length > 0 && React.createElement('div', { style: { width: '100%', height: '800px' } },
        React.createElement(Line, {
          data: {
            labels: [],
            datasets: (function() {
              var datasets = [];
              
              if (viewMode === 'both' || viewMode === 'usd') {
                datasets.push({
                  label: 'USD Value',
                  data: filteredHoldings.map(function(item) { return { x: item.timestamp, y: item.usd_holdings }; }),
                  borderColor: '#4caf50',
                  backgroundColor: '#4caf50',
                  borderWidth: 2,
                  pointRadius: filteredHoldings.map(function(item) {
                    if (!item.event) return 0;
                    var quantity = item.event.quantity || 0;
                    // Halve the min/max and scaling factor
                    return Math.min(6, Math.max(2, 2 + (Math.abs(quantity) * 150)));
                  }),
                  pointBackgroundColor: filteredHoldings.map(function(item) {
                    if (!item.event) return '#4caf50';
                    return item.event.type === 'buy' ? '#4caf50' : '#f44336';
                  }),
                  pointBorderColor: filteredHoldings.map(function(item) {
                    if (!item.event) return '#4caf50';
                    return item.event.type === 'buy' ? '#4caf50' : '#f44336';
                  }),
                  yAxisID: 'y'
                });
              }
              
              if (viewMode === 'both' || viewMode === 'btc') {
                datasets.push({
                  label: 'BTC Holdings',
                  data: filteredHoldings.map(function(item) { return { x: item.timestamp, y: item.btc_holdings }; }),
                  borderColor: '#000000',
                  backgroundColor: '#000000',
                  borderWidth: 2,
                  pointRadius: filteredHoldings.map(function(item) {
                    if (!item.event) return 0;
                    var quantity = item.event.quantity || 0;
                    return Math.min(6, Math.max(2, 2 + (Math.abs(quantity) * 150)));
                  }),
                  pointBackgroundColor: filteredHoldings.map(function(item) {
                    if (!item.event) return '#000000';
                    return item.event.type === 'buy' ? '#4caf50' : '#f44336';
                  }),
                  pointBorderColor: filteredHoldings.map(function(item) {
                    if (!item.event) return '#000000';
                    return item.event.type === 'buy' ? '#4caf50' : '#f44336';
                  }),
                  yAxisID: 'y1'
                });
              }
              
              if (viewMode === 'btc') {
                datasets.push({
                  label: 'BTC Price',
                  data: filteredHoldings.map(function(item) { return { x: item.timestamp, y: item.btc_price }; }),
                  borderColor: '#f7931a',
                  backgroundColor: '#f7931a',
                  borderWidth: 2,
                  pointRadius: 0,
                  yAxisID: 'y'
                });
              }
              
              return datasets;
            })()
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false
            },
            plugins: {
              legend: {
                position: 'top'
              },
              tooltip: {
                callbacks: {
                  title: function(context) {
                    var index = context[0].dataIndex;
                    if (filteredHoldings[index]) {
                      return formatDate(filteredHoldings[index].date);
                    }
                    return '';
                  },
                  label: function(context) {
                    var value = context.parsed.y;
                    var label = context.dataset.label || '';
                    var index = context.dataIndex;
                    var item = filteredHoldings[index];
                    
                    if (!item) return label + ': No data';
                    
                    if (label === 'USD Value') {
                      return 'USD Value: $' + Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    } else if (label === 'BTC Holdings') {
                      return 'BTC Holdings: ' + value.toFixed(8);
                    } else if (label === 'BTC Price') {
                      return 'BTC Price: $' + Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    }
                    return label;
                  },
                  afterLabel: function(context) {
                    var index = context.dataIndex;
                    var item = filteredHoldings[index];
                    if (item && item.event) {
                      return [
                        '',
                        'Event: ' + (item.event.type === 'buy' ? '🟢 Buy' : '🔴 Sell'),
                        'Quantity: ' + Math.abs(item.event.quantity).toFixed(8) + ' BTC',
                        'Price: $' + Math.round(item.event.price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                        'Total: $' + Math.round(Math.abs(item.event.quantity) * item.event.price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      ];
                    }
                    return '';
                  }
                }
              }
            },
            scales: {
              x: {
                type: 'linear',
                min: getChartStartTimestamp(filteredHoldings),
                max: getChartEndTimestamp(filteredHoldings),
                afterBuildTicks: function(scale) {
                  scale.ticks = getYearTicks(filteredHoldings);
                },
                grid: {
                  display: true
                },
                ticks: {
                  callback: function(value) {
                    return new Date(value * 1000).getUTCFullYear().toString();
                  },
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                type: 'linear',
                display: viewMode !== 'btc' || viewMode === 'btc',
                position: 'left',
                title: {
                  display: true,
                  text: viewMode === 'btc' ? 'BTC Price (USD)' : 'USD Value'
                },
                ticks: {
                  callback: function(value) {
                    return '$' + Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  }
                }
              },
              y1: {
                type: 'linear',
                display: viewMode !== 'usd',
                position: 'right',
                title: {
                  display: true,
                  text: 'BTC Holdings'
                },
                grid: {
                  drawOnChartArea: false
                }
              }
            }
          }
        })
      ),

      !loading && !error && React.createElement('div', {
        style: {
          marginTop: '30px',
          fontSize: '12px',
          color: '#999',
          textAlign: 'center'
        }
      },
        React.createElement('p', { style: { margin: '5px 0' } }, '🟢 Green circles = Buy events | 🔴 Red circles = Sell events'),
        React.createElement('p', { style: { margin: '5px 0' } }, 'Hover over data points to see transaction details')
      )
    )
  );
}
